import { defineComponent, h } from 'vue';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import BaseComponent from './BaseComponentMixin.jsx';
import {
    iconAffixClasses,
    BUTTON_BORDER_CLASS,
    tooltipLocationToAnchorSelf,
    tooltipOffsetForPlacement,
    mapSchemaButtonColorToQuasar,
    schemaButtonColorBaseTokenFromEvaluatedRaw,
    coerceSchemaBoolean,
    SENTENCE_CASE_BUTTON_CLASS,
    LITERAL_CASING_BUTTON_CLASS,
} from '../../../Shared/buttonAppearance.js';

export default {
    mixins: [BaseComponent],
    data: function () {
        return {
        }
    },
    created() {
    },
    computed: {
        text() {
            if (this.controlData.Type)
                return this.controlData.Type;

            return this.controlData.Text;
        },
        icon() {
            let icon = this.controlData.Icon;

            if (icon && icon.includes('{'))
                return 'mdi-help-circle';

            if (icon && icon.substr(0, 4) == 'mdi ') icon = icon.substr(4);
            return icon;
        },
        iconPosition() {
            if (this.controlData.IconPosition)
                return String(this.controlData.IconPosition).toLowerCase();
            return 'right';
        },
        iconGap() {
            if (this.controlData.IconGap)
                return this.controlData.IconGap;
            return 10;
        },
        flipHorizontally() {
            return this.controlData.FlipHorizontally === true || this.controlData.FlipHorizontally === 'true';
        },
        flipVertically() {
            return this.controlData.FlipVertically === true || this.controlData.FlipVertically === 'true';
        },
        border() {
            return this.controlData.Border === true || this.controlData.Border === 'true';
        },
        animation() {
            if (this.controlData.Animation)
                return this.controlData.Animation;
            return 'None';
        },
        color() {
            return mapSchemaButtonColorToQuasar(this.controlData.Color || 'secondary');
        },
        tooltip() {
            if (this.controlData.Tooltip)
                return this.controlData.Tooltip;
            return '';
        },
        tooltipLocation() {
            const loc = this.controlData.TooltipLocation;
            return loc == null || loc === '' ? 'top' : String(loc);
        },
        buttonFab() {
            return !!(this.controlData.Themes && this.controlData.Themes.includes('FloatingAction'));
        },
        showButtonBorderChrome() {
            return this.border;
        },
        qBtnFlat() {
            return this.variant === 'text' || this.variant === 'plain';
        },
        qBtnOutline() {
            return this.variant === 'outlined';
        },
        isDefault() {
            if (this.controlData.IsDefault)
                return this.controlData.IsDefault;
            return false;
        },
        isdisabled() {
            if (this.controlData.IsDisabled)
                return this.controlData.IsDisabled == 'true';
            return false;
        },
        stopPropagation() {
            if (this.controlData.StopPropagation)
                return this.controlData.StopPropagation;
            return false;
        },
        useLiteralCasing() {
            return coerceSchemaBoolean(this.controlData.UseLiteralCasing);
        },
        sentenceCaseButtonClasses() {
            if (this.useLiteralCasing)
                return { [LITERAL_CASING_BUTTON_CLASS]: true };
            return { [SENTENCE_CASE_BUTTON_CLASS]: true };
        },
        styles() {
            return {
                ...utils.getStyleHints(this.controlData.StyleHints),
                overflow: "hidden",
                whiteSpace: "nowrap",
            };
        },
        sizeStyle() {
            return utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent);
        },
        isOutlined() {
            return this.controlData.Color && String(this.controlData.Color).startsWith('outline-');
        },
        align() {
            let align;
            if (this.controlData.StyleHints && this.controlData.StyleHints.TextAlign)
                switch (this.controlData.StyleHints.TextAlign) {
                    case 'Left':
                        align = 'left';
                        break;
                    case 'Center':
                        align = 'center';
                        break;
                    case 'Right':
                        align = 'right';
                        break;
                }

            return align;
        },
        sizeOptions() {
            return this.controlData.SizeOptions;
        },
        buttonDimensionStyle() {
            const style = {};
            const width = this.sizeOptions?.Width;
            const height = this.sizeOptions?.Height;
            const unit = (units) => units == 'Percent' ? '%' : units == 'Em' ? 'em' : 'px';

            if (width?.Mode === 'Fixed' && width.Value != null && width.Value !== '')
                style.width = `${width.Value}${unit(width.Units)}`;
            if (width?.Min)
                style.minWidth = `${width.Min}${unit(width.MinUnits)}`;
            if (width?.Max)
                style.maxWidth = `${width.Max}${unit(width.MaxUnits)}`;
            if (height?.Mode === 'Fixed' && height.Value != null && height.Value !== '')
                style.height = `${height.Value}${unit(height.Units)}`;
            if (height?.Min)
                style.minHeight = `${height.Min}${unit(height.MinUnits)}`;
            if (height?.Max)
                style.maxHeight = `${height.Max}${unit(height.MaxUnits)}`;
            return style;
        },
        buttonClasses() {
            let classes = {
                ThemeRightConnector: this.controlData.Themes ? this.controlData.Themes.includes('RightConnector') : false,
                ThemeLeftConnector: this.controlData.Themes ? this.controlData.Themes.includes('LeftConnector') : false
            };

            if (this.controlData.CssClass && !this.controlData.CssClass.includes('{'))
                classes[this.controlData.CssClass] = true;

            if (this.sizeOptions && this.sizeOptions.Width && this.sizeOptions.Width.Mode === 'Fill')
                classes['full-width'] = true;

            if (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered'))) {
                classes['q-py-none'] = true;
                classes['q-px-sm'] = true;
            }

            if (this.showButtonBorderChrome && this.variant !== 'outlined')
                classes[BUTTON_BORDER_CLASS] = true;

            // Match runtime: skip q-my-xs when Height is Fixed (overflow clip + focus jump).
            classes['q-my-xs'] = this.sizeOptions?.Height?.Mode !== 'Fixed';

            return classes;
        },
        buttonStyles() {
            const styles = { ...this.buttonDimensionStyle };

            if (this.controlData.StyleHints && this.controlData.StyleHints.TextAlign)
                switch (this.controlData.StyleHints.TextAlign) {
                    case 'Left':
                        styles['justify-content'] = 'left';
                        break;
                    case 'Center':
                        styles['justify-content'] = 'center';
                        break;
                    case 'Right':
                        styles['justify-content'] = 'right';
                        break;
                }
            else if (this.controlData.StyleHints && this.controlData.StyleHints.JustifyContent)
                styles['justify-content'] = this.controlData.StyleHints.JustifyContent;

            return styles;
        },
        iconClasses() {
            return iconAffixClasses({
                flipH: this.flipHorizontally,
                flipV: this.flipVertically,
                animation: this.animation,
            });
        },
        iconSize() {
            if (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered')))
                return '12px';
            else
                return 'xs';
        },
        variant() {
            if (this.controlData.Themes && this.controlData.Themes.includes('Flat'))
                return 'text';
            else if (this.controlData.Themes && this.controlData.Themes.includes('FlatWhite'))
                return 'text';
            else if (this.controlData.Themes && this.controlData.Themes.includes('Transparent'))
                return 'plain';
            else if (schemaButtonColorBaseTokenFromEvaluatedRaw(this.controlData.Color || 'secondary') === 'link')
                return 'text';
            else if (this.isOutlined || this.controlData.Themes?.includes('CondensedBordered'))
                return 'outlined';
            else
                return 'flat';
        },
        /** Quasar QBtn size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' */
        size() {
            if (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered')))
                return 'sm';
            else
                return 'md';
        },
        iconprop() {
            return this.icon && !this.text && (this.variant == 'plain' || this.variant == 'text') ? this.icon : undefined;
        },
        /** For Quasar dense prop */
        density() {
            if ((this.icon && !this.text && (this.variant == 'plain' || this.variant == 'text')) || (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered'))))
                return true;
            else
                return false;
        },
    },
    methods: {
        renderButtonTooltip() {
            if (!this.tooltip) return null;
            const { anchor, self } = tooltipLocationToAnchorSelf(this.tooltipLocation);
            const offset = tooltipOffsetForPlacement(this.tooltipLocation);
            return (
                <QTooltip
                    transition-show="scale"
                    transition-hide="scale"
                    anchor={anchor}
                    self={self}
                    {...(offset !== undefined ? { offset } : {})}
                >
                    {utils.renderTooltipContent(this.tooltip)}
                </QTooltip>
            );
        },
        getButtonContent() {
            let content = [];

            if (this.text && this.icon) {
                if (this.iconPosition === 'left')
                    content.push(
                        <QIcon
                            name={this.icon}
                            style={{ 'margin-right': this.iconGap + 'px' }}
                            size={this.iconSize}
                            class={this.iconClasses}
                        />
                    );
                content.push(<span>{this.text}</span>);
                if (!this.iconPosition || this.iconPosition === 'right')
                    content.push(
                        <QIcon
                            name={this.icon}
                            style={{ 'margin-left': this.iconGap + 'px' }}
                            size={this.iconSize}
                            class={this.iconClasses}
                        />
                    );
            }
            else if (this.text)
                content.push(<span>{this.text}</span>);
            else if (this.icon) {
                if (this.styles.color)
                    content.push(
                        <QIcon name={this.icon} class={this.iconClasses} style={{ color: this.styles.color }} size="20px" />
                    );
                else
                    content.push(
                        <QIcon name={this.icon} class={this.iconClasses} size="20px" />
                    );
            }
            return content;
        },
    },
    props: {
    }
};
